import React, {
  createRef,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { cloneDeep, get, set } from "lodash";
import { RouteComponentProps, useHistory } from "react-router";
import { RESOURCE_NAME } from "common/authorization";
import {
  FEATURE_FLAG_NAME,
  SUBMISSION_TYPE_MODULE,
  SUPPORT_EMAIL,
} from "common/constants";
import { buildLink } from "common/routing";
import { getObjectDisplayForAttached } from "common-client/utils/customObject";
import { generateDefaultValuesObject } from "common-client/utils/defaultValues";
import {
  GetSubmissionQuery,
  useGetPropertyForSubmissionQuery,
  useGetSisdRuleDataQuery,
  useGetSubmissionQuery,
  useRegenerateAiFieldsMutation,
  useUpdateSubmissionMutation,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { setTitle } from "../../utils/title";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { Button } from "../Common/Button";
import { EmptyState } from "../Common/EmptyState";
import FullPageFormLayout from "../Common/FullPageFormLayout";
import AccessDeniedPage, {
  ACCESS_DENIED_RESOURCE,
} from "../Guest/AccessDeniedPage";
import { LinkedTask } from "../Tasks/LinkedTask";
import { DescriptionContainer } from "./__styles__/CreateSubmission";
import { ButtonContainer } from "./__styles__/Submission";
import { EmptyStateContainer } from "./__styles__/Submission";
import ActionButton from "./ActionButton";
import { FormComponent, FormDataType } from "./Form";
import SubmissionHeader from "./SubmissionHeader";
import {
  generateUpsertErrorMessage,
  hasSummary,
  useSubmissionPrevLocation,
} from "./utils";

interface URLParams {
  submissionId: string;
}

export interface EditSubmissionProps extends RouteComponentProps<URLParams> {}

const EditSubmission = ({ match }: EditSubmissionProps) => {
  const history = useHistory();
  const { authorized, user, admin, isPublic, account } =
    useContext(AuthContext);
  const { addErrorToast, addSuccessToast } = useStatusToasts();
  const [submission, setSubmission] = useState<
    GetSubmissionQuery["submission"] | null
  >(null);
  const [serverProvidedFormData, setServerProvidedFormData] =
    useState<FormDataType | null>(null);
  const [disabled, setDisabled] = useState(true);
  const [regeneratingPaths, setRegeneratingPaths] = useState<
    Record<string, boolean>
  >({});

  const { submissionId } = match.params;

  const { data, loading, error, refetch } = useGetSubmissionQuery({
    variables: { submissionId, isPublic },
    fetchPolicy: "no-cache",
    onCompleted: data => {
      setSubmission(data.submission);
    },
  });

  const [updateSubmission, { loading: updateLoading }] =
    useUpdateSubmissionMutation({
      onCompleted: data => {
        const updatedSubmission = data.updateSubmission;
        track("Submission updated", {
          submissionId: updatedSubmission.id,
          "Submission Category": submissionType.category,
          "Submission Type": submissionType.name,
        });
        addSuccessToast("Your submission has been updated.");
        history.push({
          pathname: buildLink("editSubmission", {
            submissionId: updatedSubmission.id,
          }),
        });

        return refetch();
      },
      onError: error => {
        const message = generateUpsertErrorMessage(error);
        addErrorToast(message);
      },
    });

  const propertyId = data?.submission?.property?.id;
  const { data: propertyData, loading: propertyDataLoading } =
    useGetPropertyForSubmissionQuery({
      variables: { propertyId: propertyId! },
      skip: !propertyId,
    });

  const submissionTypeModules =
    data?.submission?.submissionTypeVersion.submissionType.modules;

  const { data: sisdRuleData, loading: sisdRuleDataLoading } =
    useGetSisdRuleDataQuery({
      variables: { propertyId: propertyId! },
      skip:
        !submissionTypeModules?.includes(
          SUBMISSION_TYPE_MODULE.SUBSTANTIAL_IMPROVEMENT_SUBSTANTIAL_DAMAGE
        ) || !propertyId,
    });

  const prevLocation = useSubmissionPrevLocation();

  const property = propertyData?.property;

  const [regenerateAIFields, { loading: regenerateAIFieldsLoading }] =
    useRegenerateAiFieldsMutation();

  const dynamicDefaultValues = useMemo(() => {
    return generateDefaultValuesObject({
      property,
      user: user ?? admin,
    });
  }, [property, user, admin]);

  const propertyProp = useMemo(() => {
    return {
      FIRMInfo: sisdRuleData?.FIRMInfo,
    };
  }, [sisdRuleData?.FIRMInfo]);

  const handleOnChange = useCallback(
    ({
      isDirty,
      documentUploadsInProgress,
    }: {
      isDirty: boolean;
      documentUploadsInProgress: boolean;
    }) => {
      setDisabled(!isDirty || documentUploadsInProgress);
    },
    [setDisabled]
  );

  if (loading || propertyDataLoading || sisdRuleDataLoading) {
    return <div />;
  }

  if (error || !submission) {
    if (isPublic) {
      return <AccessDeniedPage resource={ACCESS_DENIED_RESOURCE.RECORD} />;
    }

    return (
      <FullPageFormLayout
        subtitle={""}
        prevLocation={prevLocation}
        rightContainer={<div />}
      >
        <EmptyStateContainer>
          <EmptyState
            title="This record is unavailable"
            body={
              <>
                You may not have access to this file, or it may have been
                removed.
              </>
            }
            icon="missing-file"
            buttonLinkProps={{
              label: "Get help",
              onClick: () =>
                window.open(`https://forerunner.helpscoutdocs.com/`),
            }}
            primaryButtonProps={{
              label: "Contact us",
              onClick: () => window.open(`mailto:${SUPPORT_EMAIL}`),
            }}
          />
        </EmptyStateContainer>
      </FullPageFormLayout>
    );
  }

  const submitFormRef = createRef<HTMLButtonElement>();

  const submissionType = submission.submissionTypeVersion.submissionType;

  if (isPublic) {
    setTitle(submissionType.name);
  }

  const canUpdateSubmission = authorized({
    resource: RESOURCE_NAME.SUBMISSION,
    permission: "update",
  });

  const handleRegenerateAIFields = async (
    formData: FormDataType,
    paths: string[]
  ) => {
    setRegeneratingPaths(previousPaths => {
      const clonedPreviousPaths = cloneDeep(previousPaths);
      paths.forEach(path => {
        clonedPreviousPaths[path] = true;
      });
      return clonedPreviousPaths;
    });

    await regenerateAIFields({
      variables: { data: { submissionId, formData, paths } },
      onCompleted: data => {
        setServerProvidedFormData(data.regenerateAIFields);

        // Only update the specific paths that were regenerated
        const updatedFormData = { ...formData };

        paths.forEach(path => {
          set(updatedFormData, path, get(data.regenerateAIFields, path));
        });

        setRegeneratingPaths(previousPaths => {
          const clonedPreviousPaths = cloneDeep(previousPaths);
          paths.forEach(path => {
            clonedPreviousPaths[path] = false;
          });
          return clonedPreviousPaths;
        });
        setDisabled(false);
        addSuccessToast("AI fields regenerated");
      },
    });
  };

  const handleSubmit = async (formData: FormDataType) => {
    await updateSubmission({
      variables: { data: { submissionId, formData } },
    });
  };

  const rightContainer = (
    <ButtonContainer>
      {!isPublic && (
        <Button
          size="small"
          styleVariant="primary"
          onClick={() => {
            submitFormRef.current!.click();
          }}
          disabled={!canUpdateSubmission || disabled}
          loading={updateLoading || regenerateAIFieldsLoading}
        >
          Update
        </Button>
      )}
      <ActionButton
        isEditView={true}
        submission={{
          ...submission,
          hasSummary: hasSummary(submission),
          category: submissionType.category,
        }}
        objectIsHiddenFromPublic={submission.property?.hiddenFromPublic}
      />
    </ButtonContainer>
  );

  const tasks = submission.tasks;
  const canViewTasks = authorized({
    permission: "view",
    resource: RESOURCE_NAME.TASK,
  });
  const tasksFeature = account?.featureFlags.find(
    ff => ff.feature === FEATURE_FLAG_NAME.TASKS
  );

  return (
    <FullPageFormLayout
      subtitle={submissionType.name}
      prevLocation={prevLocation}
      rightContainer={rightContainer}
      key={submission.updatedAt}
    >
      <div>
        <SubmissionHeader
          account={{
            logoUrl: account?.logoUrl ?? "",
            name: account?.name ?? "",
          }}
          objectDisplay={getObjectDisplayForAttached(submission)}
          submissionType={submissionType}
        >
          {submissionType.description && (
            <DescriptionContainer markdown={submissionType.description} />
          )}
        </SubmissionHeader>
        {tasksFeature?.isEnabled && canViewTasks && tasks.length > 0 && (
          <LinkedTask tasks={tasks} submission={submission} />
        )}
        <FormComponent
          readonly={!canUpdateSubmission}
          submissionType={{
            currentVersion: submission.submissionTypeVersion,
            modules: submission.submissionTypeVersion.submissionType.modules,
            id: submission.submissionTypeVersion.submissionType.id,
          }}
          existingSubmission={submission}
          relatedSubmissions={submission.relatedSubmissions}
          onSubmit={handleSubmit}
          onChange={handleOnChange}
          submitFormRef={submitFormRef}
          dynamicDefaultValues={dynamicDefaultValues}
          account={sisdRuleData?.account}
          property={propertyProp}
          loadingFormPaths={regeneratingPaths}
          regenerateAIFields={handleRegenerateAIFields}
          serverProvidedFormData={serverProvidedFormData ?? undefined}
        />
      </div>
    </FullPageFormLayout>
  );
};

export default EditSubmission;
